import { useCallback } from "react";
import type { BaseEntity, FieldValue } from "../../types";

interface UseBulkEditCallbacksProps<TData extends BaseEntity> {
  data: TData[];
  onInlineEdit?: (payload: Record<string, FieldValue>) => Promise<void>;
}

export function useBulkEditCallbacks<TData extends BaseEntity>({
  data,
  onInlineEdit,
}: UseBulkEditCallbacksProps<TData>) {
  const handleBulkEditField = useCallback(
    async (fieldId: string, value: FieldValue, itemIds: string[]) => {
      if (!onInlineEdit) return;

      const selectedItems = data.filter((item) => itemIds.includes(item.id));

      for (const item of selectedItems) {
        try {
          const updatePayload = {
            ...item,
            [fieldId]: value,
            _isBulkEdit: true,
          };
          await onInlineEdit(updatePayload);
        } catch (error) {
          console.error(`Failed to update item ${item.id}:`, error);
        }
      }
    },
    [onInlineEdit, data]
  );

  const handleBulkEditAllFiltered = useCallback(
    async (fieldId: string, value: FieldValue, _filterCriteria?: Record<string, unknown>) => {
      if (!onInlineEdit) return;

      for (const item of data) {
        try {
          const updatePayload = {
            ...item,
            [fieldId]: value,
            _isBulkEdit: true,
          };
          await onInlineEdit(updatePayload);
        } catch (error) {
          console.error(`Failed to update filtered item ${item.id}:`, error);
        }
      }
    },
    [onInlineEdit, data]
  );

  return {
    handleBulkEditField,
    handleBulkEditAllFiltered,
  };
}
